'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/app/Components/AuthProvider';
import { useRouter } from 'next/navigation';
import { RutgersCourse, UserAPCredit } from '@/app/types/interfaces';
import PrerequisiteChecker from '@/app/Components/PrerequisiteChecker';
import APCreditInput from '@/app/Components/APCreditInput';
import TranscriptUpload from '@/app/Components/TranscriptUpload';
import { loadCatalogCourses as getParsedCourses } from '@/app/services/courseCatalogClient';
import {
  loadUserAcademicData,
  saveCompletedCourses,
  saveTranscriptCourses,
  saveAPCredits,
  buildCoursesForPrereq,
  saveCourseOverrides
} from '@/app/services/userAcademicData';
import { saveCourseGrades } from '@/app/services/userAcademicData';
import type { CourseGradeEntry } from '@/app/services/userAcademicData';
import { getRutgersCoursesByBaseIds } from '@/app/services/userAcademicData';
import { formatBaseIdDisplay, fuzzyMatchScore, partialMatch } from '@/app/lib/utils';
import { RUTGERS_GRADE_OPTIONS } from '@/app/lib/rutgersGpa';
import { useDebounce } from 'use-debounce';
import { readPrerequisiteLaunchQuery, readPrerequisiteLaunchView } from './launchQuery';
import { InfoTooltip } from '@/app/Components/InfoTooltip';
import { PageLoading } from '@/app/Components/LoadingBubbles';
import {
  AcademicCapIcon,
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

export default function PrerequisiteCheckerPage() {
  return <PrerequisiteCheckerWorkspace />;
}

export function PrerequisiteCheckerWorkspace({ embedded = false, initialTab }: { embedded?: boolean; initialTab?: 'completed' | 'ap-credits' | 'checker' }) {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [completedCourses, setCompletedCourses] = useState<RutgersCourse[]>([]);
  const [apCredits, setAPCredits] = useState<UserAPCredit[]>([]);
  const [overrides, setOverrides] = useState<string[]>([]);
  const [courseGrades, setCourseGrades] = useState<CourseGradeEntry[]>([]);
  const [allCourses, setAllCourses] = useState<RutgersCourse[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [launchQuery, setLaunchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<RutgersCourse | null>(null);
  const [activeTab, setActiveTab] = useState<'completed' | 'ap-credits' | 'checker'>(initialTab ?? (embedded ? 'completed' : 'checker'));
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [newOverride, setNewOverride] = useState('');
  const [recordSaveError, setRecordSaveError] = useState<string | null>(null);
  const ownerRef = useRef<string | null>(null);
  const completedBaselineRef = useRef<string[] | null>(null);
  const apBaselineRef = useRef<UserAPCredit[] | null>(null);
  const overrideBaselineRef = useRef<string[] | null>(null);
  const gradeBaselineRef = useRef<CourseGradeEntry[] | null>(null);
  const recordLoadRevisionRef = useRef(0);
  const pendingWritesRef = useRef(0);
  const ownerSessionRef = useRef(0);

  const markUnsaved = useCallback(() => {
    setRecordSaveError(current => current ?? 'Some academic edits were not saved. The record may have changed. Reload it to review the saved version.');
  }, []);

  useEffect(() => {
    if (embedded) return;
    const query = readPrerequisiteLaunchQuery(window.location.search);
    const view = readPrerequisiteLaunchView(window.location.search);
    if (query) {
      setLaunchQuery(query);
      setActiveTab('checker');
    } else if (view) {
      setActiveTab(view);
    }
  }, [embedded]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      router.push('/login');
    }
  }, [user, loading, router]);

  // Load user data when component mounts
  useEffect(() => {
    const ownerId = user?.id ?? null;
    if (ownerRef.current !== ownerId) {
      ownerRef.current = ownerId;
      ownerSessionRef.current += 1;
      completedBaselineRef.current = null;
      apBaselineRef.current = null;
      overrideBaselineRef.current = null;
      gradeBaselineRef.current = null;
      recordLoadRevisionRef.current += 1;
      pendingWritesRef.current = 0;
      setCompletedCourses([]);
      setAPCredits([]);
      setOverrides([]);
      setCourseGrades([]);
      setLastSaved(null);
      setRecordSaveError(null);
    }
    if (user) {
      void loadUserData();
      void loadCourses();
    }
  }, [user?.id]);

  const loadUserData = useCallback(async ({ background = false }: { background?: boolean } = {}) => {
    if (!user?.id) return false;
    const ownerId = user.id;
    const revision = ++recordLoadRevisionRef.current;
    // A post-save refresh must retain the mounted transcript review on failure.
    if (!background) setIsLoading(true);
    try {
      const academicData = await loadUserAcademicData(ownerId, { fresh: true });
      if (ownerRef.current !== ownerId || revision !== recordLoadRevisionRef.current || pendingWritesRef.current > 0) return false;

      if (academicData) {

        setCompletedCourses(academicData.completedCourses);
        setAPCredits(academicData.apCredits);
        setOverrides(academicData.overrides || []);
        setCourseGrades(academicData.courseGrades || []);
        setLastSaved(academicData.lastUpdated);
        completedBaselineRef.current = academicData.completedCourses.map(course => course.baseId);
        apBaselineRef.current = academicData.apCredits;
        overrideBaselineRef.current = academicData.overrides || [];
        gradeBaselineRef.current = academicData.courseGrades || [];
        setRecordSaveError(null);
        return true;
      } else {
        setRecordSaveError('The saved academic record could not be refreshed. Your edits remain on this screen.');
        return false;
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      if (ownerRef.current === ownerId && revision === recordLoadRevisionRef.current) setRecordSaveError('The saved academic record could not be refreshed. Your edits remain on this screen.');
      return false;
    } finally {
      if (ownerRef.current === ownerId && revision === recordLoadRevisionRef.current) setIsLoading(false);
    }
  }, [user?.id]);

  const loadCourses = useCallback(async () => {
    try {
      const courses = await getParsedCourses();
      setAllCourses(courses);
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  }, []);

  const saveCompletedCoursesToDB = useCallback(async (courses: RutgersCourse[]) => {
    const loadedBaseIds = completedBaselineRef.current;
    if (!user?.id || ownerRef.current !== user.id || !loadedBaseIds || recordSaveError) {
      markUnsaved();
      return false;
    }

    pendingWritesRef.current += 1;
    const ownerSession = ownerSessionRef.current;
    recordLoadRevisionRef.current += 1;
    setIsSaving(true);
    try {
      const success = await saveCompletedCourses(user.id, courses, loadedBaseIds);
      if (ownerSession !== ownerSessionRef.current || ownerRef.current !== user.id) return false;
      if (success) {
        completedBaselineRef.current = courses.map(course => course.baseId);
        setLastSaved(new Date().toISOString());
      } else {
        markUnsaved();
      }
      return success;
    } catch (error) {
      console.error('Error saving completed courses:', error);
      if (ownerSession === ownerSessionRef.current) markUnsaved();
      return false;
    } finally {
      if (ownerSession === ownerSessionRef.current) {
        pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
        recordLoadRevisionRef.current += 1;
        setIsSaving(pendingWritesRef.current > 0);
      }
    }
  }, [markUnsaved, recordSaveError, user?.id]);

  const saveAPCreditsToDB = useCallback(async (credits: UserAPCredit[]) => {
    const loadedCredits = apBaselineRef.current;
    if (!user?.id || ownerRef.current !== user.id || !loadedCredits || recordSaveError) {
      markUnsaved();
      return false;
    }

    pendingWritesRef.current += 1;
    const ownerSession = ownerSessionRef.current;
    recordLoadRevisionRef.current += 1;
    setIsSaving(true);
    try {
      const success = await saveAPCredits(user.id, credits, loadedCredits);
      if (ownerSession !== ownerSessionRef.current || ownerRef.current !== user.id) return false;
      if (success) {
        apBaselineRef.current = credits;
        setLastSaved(new Date().toISOString());
      } else markUnsaved();
      return success;
    } catch (error) {
      console.error('Error saving AP credits:', error);
      if (ownerSession === ownerSessionRef.current) markUnsaved();
      return false;
    } finally {
      if (ownerSession === ownerSessionRef.current) {
        pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
        recordLoadRevisionRef.current += 1;
        setIsSaving(pendingWritesRef.current > 0);
      }
    }
  }, [markUnsaved, recordSaveError, user?.id]);

  const saveOverridesToDB = useCallback(async (codes: string[]) => {
    const loadedOverrides = overrideBaselineRef.current;
    if (!user?.id || ownerRef.current !== user.id || !loadedOverrides || recordSaveError) {
      markUnsaved();
      return false;
    }
    pendingWritesRef.current += 1;
    const ownerSession = ownerSessionRef.current;
    recordLoadRevisionRef.current += 1;
    setIsSaving(true);
    try {
      const ok = await saveCourseOverrides(user.id, codes, loadedOverrides);
      if (ownerSession !== ownerSessionRef.current || ownerRef.current !== user.id) return false;
      if (ok) {
        overrideBaselineRef.current = codes;
        setLastSaved(new Date().toISOString());
      } else markUnsaved();
      return ok;
    } catch (e) {
      console.error('Error saving overrides:', e);
      if (ownerSession === ownerSessionRef.current) {
        markUnsaved();
      }
      return false;
    } finally {
      if (ownerSession === ownerSessionRef.current) {
        pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
        recordLoadRevisionRef.current += 1;
        setIsSaving(pendingWritesRef.current > 0);
      }
    }
  }, [markUnsaved, recordSaveError, user?.id]);

  // Advanced course search with three-phase matching (same as explore and scheduling pages)
  const filteredCourses = useMemo(() => {
    const term = debouncedSearchQuery.toLowerCase().trim();
    if (!term) return allCourses;

    const seen = new Set<string>();

    // Phase 1: Direct matches
    // Phase 2: Fuzzy matches (if no direct matches)
    // Phase 3: Partial matches (if no fuzzy matches)
    const directMatches: RutgersCourse[] = [];
    const fuzzyMatches: Array<{ course: RutgersCourse; score: number }> = [];
    const partialMatches: RutgersCourse[] = [];

    for (const course of allCourses) {
      if (seen.has(course.baseId)) continue;

      const name = String(course.courseName || '').toLowerCase();
      const dept = String(course.department || '').toLowerCase();
      const num = String(course.class_number || '').toLowerCase();
      const base = String(course.baseId || '').toLowerCase();
      const fullTitle = String(course.fullTitle || '').toLowerCase();
      const instructors = (course.instructors || []).map(inst => inst.toLowerCase());
      const searchFields = [name, dept, num, base, fullTitle, ...instructors];

      // Handle colon-separated search (e.g., "640:250")
      let isDirectMatch = false;
      if (term.includes(':')) {
        const parts = term.split(':');
        if (parts.length === 2) {
          const searchDept = parts[0].trim();
          const searchNum = parts[1].trim();
          isDirectMatch = dept.includes(searchDept) && num.includes(searchNum);
        }
      } else {
        // Check for direct substring matches
        isDirectMatch = searchFields.some(field => field.includes(term));
      }

      if (isDirectMatch) {
        seen.add(course.baseId);
        directMatches.push(course);
        continue; // Skip further checks for this course
      }

      // Try fuzzy matching (only if no direct matches found yet)
      const fuzzyScore = fuzzyMatchScore(term, searchFields, { threshold: 0.6 });
      if (fuzzyScore > 0) {
        seen.add(course.baseId);
        fuzzyMatches.push({
          course,
          score: fuzzyScore
        });
        continue; // Skip partial matching for this course
      }

      // Try partial matching (word-by-word)
      const nameMatch = course.courseName && partialMatch(term, course.courseName);
      const fullTitleMatch = course.fullTitle && partialMatch(term, course.fullTitle);
      if (nameMatch || fullTitleMatch) {
        seen.add(course.baseId);
        partialMatches.push(course);
      }
    }

    // Combine results: direct matches first, then fuzzy (sorted by score), then partial
    const out: RutgersCourse[] = [];
    if (directMatches.length > 0) {
      out.push(...directMatches);
    } else if (fuzzyMatches.length > 0) {
      // Sort fuzzy matches by score (highest first) and add top results
      fuzzyMatches.sort((a, b) => b.score - a.score);
      out.push(...fuzzyMatches.slice(0, 50).map(m => m.course)); // Limit to top 50
    } else if (partialMatches.length > 0) {
      out.push(...partialMatches);
    }

    return out;
  }, [allCourses, debouncedSearchQuery]);

  // Remove duplicates by grouping by baseId and taking the first occurrence
  const uniqueFilteredCourses = useMemo(() => {
    return filteredCourses.reduce((unique, course) => {
      const existingCourse = unique.find(c => c.baseId === course.baseId);
      if (!existingCourse) {
        unique.push(course);
      }
      return unique;
    }, [] as RutgersCourse[]);
  }, [filteredCourses]);

  // Accept a baseId and resolve to a representative RutgersCourse
  const handleAddCourse = useCallback(async (courseBaseId: string | RutgersCourse) => {
    const baseId = typeof courseBaseId === 'string' ? courseBaseId : courseBaseId.baseId;
    if (completedCourses.some(c => c.baseId === baseId)) {
      setShowAddCourse(false);
      setSelectedCourse(null);
      return;
    }
    // Prefer existing allCourses list; fallback to resolver
    let found = allCourses.find(c => c.baseId === baseId) || null;
    if (!found) {
      const resolved = await getRutgersCoursesByBaseIds([baseId]);
      found = resolved[0] || null;
    }
    if (!found) return;
    const updatedCourses = [...completedCourses, found];
    setCompletedCourses(updatedCourses);
    await saveCompletedCoursesToDB(updatedCourses);
    setShowAddCourse(false);
    setSelectedCourse(null);
  }, [allCourses, completedCourses, saveCompletedCoursesToDB]);

  const handleRemoveCourse = useCallback(async (courseId: string) => {
    const updatedCourses = completedCourses.filter(c => c.id !== courseId);
    setCompletedCourses(updatedCourses);

    // Save to database
    await saveCompletedCoursesToDB(updatedCourses);
  }, [completedCourses, saveCompletedCoursesToDB]);

  const handleCourseSelect = useCallback((course: RutgersCourse) => {
    setSelectedCourse(course);
  }, []);

  const handleAPCreditsChange = useCallback(async (newAPCredits: UserAPCredit[]) => {
    const ownerSession = ownerSessionRef.current;
    setAPCredits(newAPCredits);
    try {
      setIsSaving(true);
      return await saveAPCreditsToDB(newAPCredits);
    } catch (error) {
      console.error('Error in handleAPCreditsChange:', error);
      if (ownerSession === ownerSessionRef.current) markUnsaved();
      return false;
    } finally {
      if (ownerSession === ownerSessionRef.current) setIsSaving(pendingWritesRef.current > 0);
    }
  }, [markUnsaved, saveAPCreditsToDB]);

  const renderEmptyState = (
    icon: React.ReactNode,
    title: string,
    description: string,
    cta?: { label: string; onClick: () => void }
  ) => (
    <div className="rounded-feature border border-dashed border-slate-300 bg-white px-6 py-14 text-center shadow-e1 dark:border-slate-700 dark:bg-slate-900/70">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-card bg-slate-100 dark:bg-slate-800">
        {icon}
      </div>
      <p className="text-lg font-semibold text-slate-900 dark:text-white">{title}</p>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">{description}</p>
      {cta && (
        <button
          type="button"
          onClick={cta.onClick}
          className="mt-5 rounded-card bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-500"
        >
          {cta.label}
        </button>
      )}
    </div>
  );

  const completedForChecks = useMemo(() => {
    return buildCoursesForPrereq({
      completedCourses,
      apCredits,
      overrides,
      courseGrades: [],
      lastUpdated: lastSaved || new Date().toISOString(),
    });
  }, [completedCourses, apCredits, overrides, lastSaved]);

  if (loading || isLoading) {
    return <PageLoading title="Loading academic history" description="Preparing your prerequisite workspace." />;
  }

  if (!user) {
    return null; // Will redirect to login
  }

  return (
    <div className={embedded ? "pb-24" : "pb-8"}>
      <div className={embedded ? "mx-auto max-w-7xl" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"}>
        {!embedded ? <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[var(--ss-text)] md:text-3xl">Prerequisite Checker</h1>
            <p className="mt-1 text-sm text-[var(--ss-text-muted)]">Check prerequisites against your saved academic record.</p>
          </div>
          <div className="flex items-center gap-2">
            {isSaving ? <span role="status" className="text-sm text-[var(--ss-text-muted)]">Saving…</span> : null}
            <InfoTooltip title="Prerequisite checks" text="Checks use your completed courses, applied AP equivalents, and course overrides. Manage those records in the adjacent tabs. Overrides count for prerequisites only; they do not add a grade." />
          </div>
        </header> : null}
        {recordSaveError ? <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-control border border-[var(--ss-warning)] bg-[var(--ss-warning-bg)] p-3 text-sm text-[var(--ss-text)]"><span>{recordSaveError} Reloading replaces the edits shown here.</span><button type="button" onClick={() => void loadUserData()} className="min-h-12 rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface)] px-4 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">Reload academic record</button></div> : null}

        {/* The embedded Plan workspace already owns navigation. */}
        {!embedded && <div className="mb-6">
          <div className="rounded-feature border border-slate-200 bg-white p-2 shadow-e1 dark:border-white/10 dark:bg-slate-900/80">
            <nav aria-label="Academic record tools" className="flex flex-nowrap gap-1 overflow-x-auto">
              {([
                ['checker', 'Check prerequisites'],
                ['completed', `Completed (${completedCourses.length})`],
                ['ap-credits', `AP credit (${apCredits.filter(c => c.isApplied).length})`],
                ['gpa-calculator', 'GPA Calculator'],
              ] as const).map(([tab, label]) => (
                <button
                  key={tab}
                  type="button"
                  aria-pressed={tab === 'gpa-calculator' ? undefined : activeTab === tab}
                  onClick={() => tab === 'gpa-calculator' ? router.push('/gpa-calculator') : setActiveTab(tab)}
                  className={`min-h-12 shrink-0 rounded-card px-4 py-2.5 text-sm font-medium transition ${
                    activeTab === tab
                      ? 'bg-red-100 text-red-700 ring-1 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-900/40'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        }
        {/* Tab Content */}
        {activeTab === 'completed' && (
          <div className="space-y-6 mb-8">
            {/* Transcript Upload Section */}
            <TranscriptUpload
              key={user?.id}
              onExtractCourses={async (courses, earned) => {
                if (earned) {
                  const ownerId = user?.id, session = ownerSessionRef.current;
                  const loadedIds = completedBaselineRef.current, loadedGrades = gradeBaselineRef.current;
                  const current = () => ownerRef.current === ownerId && ownerSessionRef.current === session;
                  if (!ownerId || !loadedIds || !loadedGrades || recordSaveError || pendingWritesRef.current > 0 || !current()) return false;
                  pendingWritesRef.current += 1; recordLoadRevisionRef.current += 1; setIsSaving(true);
                  let released = false;
                  const release = () => {
                    if (!released && current()) { pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1); setIsSaving(pendingWritesRef.current > 0); }
                    released = true;
                  };
                  try {
                    const saved = await saveTranscriptCourses(ownerId, earned, loadedIds, loadedGrades, current);
                    if (!current()) return false;
                    if (!saved) { markUnsaved(); return false; }
                    // Release only this settled write before the existing guarded reload.
                    // Other pending writes still prevent the reload from publishing stale data.
                    release();
                    const refreshed = await loadUserData({ background: true });
                    return current() && refreshed;
                  } finally { release(); }
                }
                // Merge with existing courses, avoiding duplicates
                const newCourses = courses.filter(c =>
                  !completedCourses.some(existing => existing.baseId === c.baseId)
                );
                if (newCourses.length > 0) {
                  const updatedCourses = [...completedCourses, ...newCourses];
                  setCompletedCourses(updatedCourses);
                  await saveCompletedCoursesToDB(updatedCourses);
                } else {
                }
              }}
              onExtractAPCredits={async (credits) => {
                // Course ingestion may have refreshed AP data since this callback
                // was captured. Import is additive to that current owner baseline.
                const ownerId = user?.id, ownerSession = ownerSessionRef.current;
                const current = () => ownerRef.current === ownerId && ownerSessionRef.current === ownerSession;
                const loadedCredits = apBaselineRef.current;
                if (!ownerId || !current() || !loadedCredits || recordSaveError || pendingWritesRef.current > 0) return false;
                const newCredits = credits.filter(credit =>
                  !loadedCredits.some(existing =>
                    existing.examId === credit.examId && existing.score === credit.score
                  )
                );
                if (newCredits.length > 0) {
                  const saved = await handleAPCreditsChange([...loadedCredits, ...newCredits]);
                  return current() && saved;
                }
                return true;
              }}
              existingCourses={completedCourses}
            />

            <div className="bg-white dark:bg-slate-800 rounded-control shadow-e1">
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                    Completed Courses ({completedCourses.length})
                  </h2>

                  <button
                    onClick={() => setShowAddCourse(true)}
                    className="flex min-h-12 items-center px-4 py-2 bg-red-600 text-white rounded-control hover:bg-red-700 transition-colors"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Add Completed Course
                  </button>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  These courses will be used to check prerequisites for other courses
                </p>
              </div>

              <div className="p-6">
              {completedCourses.length === 0 ? (
                renderEmptyState(
                  <AcademicCapIcon className="h-8 w-8 text-slate-400 dark:text-slate-500" />,
                  'No completed courses added yet',
                  'Add your prior coursework here to unlock prerequisite checks, GPA input, and transcript-backed history.',
                  { label: 'Add Completed Course', onClick: () => setShowAddCourse(true) }
                )
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {completedCourses.map((course) => (
                    <div
                      key={course.id}
                      className="bg-white dark:bg-slate-800 rounded-control p-3 border border-slate-200 dark:border-slate-700 shadow-e1 transition-all hover:shadow-e2 hover:border-red-100 dark:hover:border-red-900/30"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 dark:text-white text-sm truncate" title={course.courseName}>
                            {course.courseName}
                          </h3>
                          <div className="mt-2 flex items-center justify-between">
                            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">
                              {course.credits} credits
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-slate-500">Grade</span>
                              <select
                                value={
                                  (courseGrades.find(g => g.code.replace(/[^0-9]/g, '') === course.baseId)?.grade) || ''
                                }
                                onChange={async (e) => {
                                  if (!user?.id) return;
                                  const ownerSession = ownerSessionRef.current;
                                  const code = course.baseId;
                                  const next: CourseGradeEntry = { code, grade: e.target.value as any };
                                  const byCode = new Map<string, CourseGradeEntry>(courseGrades.map(g => [g.code.replace(/[^0-9]/g, ''), g]));
                                  byCode.set(code, { ...byCode.get(code), ...next });
                                  const merged = Array.from(byCode.values());
                                  setCourseGrades(merged);
                                  const loadedGrades = gradeBaselineRef.current;
                                  if (ownerRef.current !== user.id || !loadedGrades || recordSaveError) {
                                    markUnsaved();
                                    return;
                                  }
                                  pendingWritesRef.current += 1;
                                  recordLoadRevisionRef.current += 1;
                                  setIsSaving(true);
                                  try {
                                    const ok = await saveCourseGrades(user.id, merged, loadedGrades);
                                    if (ownerSession !== ownerSessionRef.current || ownerRef.current !== user.id) return;
                                    if (ok) {
                                      gradeBaselineRef.current = merged;
                                      setLastSaved(new Date().toISOString());
                                    } else markUnsaved();
                                  } catch {
                                    if (ownerSession === ownerSessionRef.current) markUnsaved();
                                  } finally {
                                    if (ownerSession === ownerSessionRef.current) {
                                      pendingWritesRef.current -= 1;
                                      recordLoadRevisionRef.current += 1;
                                      setIsSaving(pendingWritesRef.current > 0);
                                    }
                                  }
                                }}
                                className="text-xs px-1.5 py-0.5 border border-slate-200 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-red-500"
                              >
                                <option value="">—</option>
                                {RUTGERS_GRADE_OPTIONS.map((grade) => <option key={grade} value={grade}>{grade === 'P' ? 'P (Pass)' : grade}</option>)}
                              </select>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveCourse(course.id)}
                          className="text-slate-300 hover:text-red-500 transition-colors shrink-0"
                          title="Remove course"
                          aria-label={`Remove ${course.courseName}`}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                  ))}
                </div>
              )}

              {/* Course Overrides */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Course Overrides</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">Search a course and mark it satisfied without a grade (counts for prerequisites only). Existing earned grades are preserved.</p>
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search courses to override…"
                    value={newOverride}
                    onChange={(e) => setNewOverride(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-control bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
                {newOverride && (
                  <div className="max-h-64 overflow-y-auto mb-3 space-y-2">
                    {uniqueFilteredCourses
                      .filter(c => {
                        const q = newOverride.toLowerCase();
                        return (
                          c.courseName.toLowerCase().includes(q) ||
                          c.department.toLowerCase().includes(q) ||
                          c.class_number.toLowerCase().includes(q)
                        );
                      })
                      .slice(0, 25)
                      .map(course => (
                        <div
                          key={`ov-${course.id}`}
                          className="p-3 border border-slate-200 dark:border-slate-600 rounded-control hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
                          onClick={async () => {
                            const code = course.baseId; // store as baseId
                            if (overrides.includes(code)) { setNewOverride(''); return; }
                            const updated = [...overrides, code];
                            setOverrides(updated);
                            await saveOverridesToDB(updated);
                            setNewOverride('');
                          }}
                        >
                          <div className="font-medium text-slate-900 dark:text-white">
                            {course.courseName}
                          </div>
                          <div className="text-sm text-slate-600 dark:text-slate-400">
                            {course.department}:{course.class_number}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
                {overrides.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {overrides.map(code => (
                      <span key={code} className="inline-flex items-center px-2 py-1 rounded-control text-xs bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-600">
                        {code.slice(0,2)}:{code.slice(2,5)}:{code.slice(5)}
                        <span className="ml-2 text-slate-500">N/A</span>
                        <button
                          type="button"
                          onClick={async () => {
                            const updated = overrides.filter(c => c !== code);
                            setOverrides(updated);
                            await saveOverridesToDB(updated);
                          }}
                          className="ml-1 text-slate-400 hover:text-red-500"
                          aria-label={`Remove override ${code}`}
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {completedCourses.some(course => !courseGrades.some(entry =>
                entry.code.replace(/[^0-9]/g, '') === course.baseId.replace(/[^0-9]/g, '') && entry.grade
              )) && <p className="mt-4 text-sm text-[var(--ss-text-muted)]">Enter each earned grade from your academic record. Leave unknown grades unselected.</p>}
            </div>
          </div>
          </div>
        )}

        {activeTab === 'ap-credits' && (
          <APCreditInput
            onAPCreditsChange={handleAPCreditsChange}
            completedCourses={completedCourses}
            initialAPCredits={apCredits}
            onAddCompletedCourse={handleAddCourse}
          />
        )}

        {activeTab === 'checker' && (
          <>
            <PrerequisiteChecker
              completedCourses={completedForChecks}
              apCredits={apCredits}
              onCourseSelect={handleCourseSelect}
              initialQuery={launchQuery}
            />
          </>
        )}

        {/* Add Course Modal */}
        {showAddCourse && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 backdrop-blur-sm">
            <div className="max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-feature border border-white/70 bg-white shadow-e3 shadow-black/20 dark:border-white/10 dark:bg-slate-900">
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Add Completed Course
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowAddCourse(false)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    aria-label="Close add completed course modal"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search courses by name, dept:number (e.g. 640:250), or title…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-control bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div className="max-h-96 overflow-y-auto">
                  <div className="space-y-2">
                    {uniqueFilteredCourses.slice(0, 50).map((course) => {
                      // Get unique campuses for this course
                      const campuses = [...new Set(allCourses.filter(c => c.baseId === course.baseId).map(c => c.campus))];

                      return (
                        <div
                          key={course.id}
                          className="p-3 border border-slate-200 dark:border-slate-600 rounded-control hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
                          onClick={() => handleAddCourse(course)}
                        >
                          <h4 className="font-medium text-slate-900 dark:text-white">
                            {course.courseName}
                          </h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            {course.department}:{course.class_number} • {course.credits} credits
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            {campuses.join(', ')}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
